import { CardRarityOdds, rarityOddsProblem } from './cardRarityOdds';

export type CardRarityMode = 'global' | 'division';

/** Odds per rarity ("0".."3") for each division tier ("1".."21"). */
export type DivisionRarityOdds = Record<string, CardRarityOdds>;

export const DIVISION_TIERS: string[] = Array.from({ length: 21 }, (_, i) => String(i + 1));

/**
 * GDD v1.03 "Player Card Drop rate": Rookie / Semi-Pro / Pro / Legendary (%) per division tier.
 * Same values as the server's DEFAULT_DIVISION_RARITY_WEIGHTS. Tier 3 (Rookie League I): the GDD
 * prints Semi-Pro 21.25, which makes the row add up to 100.25; it is corrected to 21 here.
 */
const GDD_ROWS: Array<[number, number, number, number]> = [
  [80, 18, 1.5, 0.5],
  [78, 19.75, 1.75, 0.5],
  [76, 21, 2, 1],
  [74, 22.5, 2.5, 1],
  [72, 23, 3, 2],
  [70, 24, 4, 2],
  [68, 25, 4, 3],
  [66, 26, 5, 3],
  [64, 27, 5, 4],
  [62, 28, 6, 4],
  [60, 29, 6, 5],
  [55, 32, 8, 5],
  [50, 34, 8, 8],
  [45, 36, 11, 8],
  [40, 37, 13, 10],
  [35, 38, 15, 12],
  [30, 39, 17, 14],
  [25, 39, 20, 16],
  [20, 39.5, 22.5, 18],
  [15, 40, 25, 20],
  [10, 38, 27, 25],
];

export const GDD_DIVISION_RARITY_ODDS: DivisionRarityOdds = Object.fromEntries(
  GDD_ROWS.map((row, i) => [String(i + 1), { '0': row[0], '1': row[1], '2': row[2], '3': row[3] }])
);

/** Division names (game-config.data.ts), used when the server does not send `divisions`. */
export const DEFAULT_DIVISION_NAMES: Record<string, string> = Object.fromEntries(
  [
    'Rookie League III',
    'Rookie League II',
    'Rookie League I',
    'Semi-Pro III',
    'Semi-Pro II',
    'Semi-Pro I',
    'Pro III',
    'Pro II',
    'Pro I',
    'National III',
    'National II',
    'National I',
    'Continental III',
    'Continental II',
    'Continental I',
    'Global III',
    'Global II',
    'Global I',
    'Legendary III',
    'Legendary II',
    'Legendary I',
  ].map((name, i) => [String(i + 1), name])
);

/** A fresh copy of the GDD table (so edits never touch the constant). */
export function gddDivisionOdds(): DivisionRarityOdds {
  return Object.fromEntries(DIVISION_TIERS.map((t) => [t, { ...GDD_DIVISION_RARITY_ODDS[t] }]));
}

/**
 * The table as the server sent it, with any missing or malformed division row filled from the
 * GDD table. Returns the GDD table when the server sent nothing usable (older backend).
 */
export function divisionOddsFromServer(value: unknown): DivisionRarityOdds {
  const table = gddDivisionOdds();
  if (!value || typeof value !== 'object' || Array.isArray(value)) return table;
  const src = value as Record<string, unknown>;
  for (const t of DIVISION_TIERS) {
    const row = src[t];
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      const r = row as Record<string, unknown>;
      table[t] = Object.fromEntries(
        ['0', '1', '2', '3'].map((k) => [
          k,
          typeof r[k] === 'number' ? (r[k] as number) : undefined,
        ])
      );
    }
  }
  return table;
}

export function rarityModeFromServer(value: unknown): CardRarityMode {
  return value === 'division' ? 'division' : 'global';
}

/** Every division row that the server would reject, as `{ tier, problem }`, in tier order. */
export function divisionOddsProblems(
  table: DivisionRarityOdds
): Array<{ tier: string; problem: string }> {
  const out: Array<{ tier: string; problem: string }> = [];
  for (const t of DIVISION_TIERS) {
    const problem = rarityOddsProblem(table[t] ?? {});
    if (problem) out.push({ tier: t, problem });
  }
  return out;
}

/** The table in the shape the PUT expects (only call once divisionOddsProblems is empty). */
export function divisionOddsForSave(
  table: DivisionRarityOdds
): Record<string, Record<string, number>> {
  return Object.fromEntries(
    DIVISION_TIERS.map((t) => [
      t,
      Object.fromEntries(['0', '1', '2', '3'].map((k) => [k, table[t][k] as number])),
    ])
  );
}

export function sameDivisionOdds(a: DivisionRarityOdds, b: DivisionRarityOdds): boolean {
  return DIVISION_TIERS.every((t) => ['0', '1', '2', '3'].every((k) => a[t]?.[k] === b[t]?.[k]));
}
